import { useMemo } from 'react';
import { FileUploadMetaDataExtractionDto } from '@asset-metrix/portal-gateway-client-js';
import { JsonSchema } from '@sagold/react-json-editor';

import {
  METADATA_EXTRACTION_FROM_AI_VALUE,
  METADATA_EXTRACTION_FROM_USER_VALUE,
  METADATA_EXTRACTION_TYPE,
} from '../constants';
import { allOfEntryTargetsAnyKey } from '../step2SchemaProperties';

type JsonSchemaDefinition = NonNullable<JsonSchema['properties']>[string];

// reassigns original properties from the original schema, undoing changes made by disableProperty
function reassignOriginalProperties(
  value: JsonSchemaDefinition,
  originalProperty: JsonSchemaDefinition
): JsonSchemaDefinition {
  if (typeof value !== 'object' || typeof originalProperty !== 'object') {
    return value;
  }

  const { type: _type, anyOf: _anyOf, ...rest } = value;

  return {
    ...rest,
    ...(originalProperty.minItems !== undefined && {
      minItems: originalProperty.minItems,
    }),
    ...(originalProperty.minLength !== undefined && {
      minLength: originalProperty.minLength,
    }),
    ...(originalProperty.type !== undefined && {
      type: originalProperty.type,
    }),
    ...(originalProperty.anyOf !== undefined && {
      anyOf: originalProperty.anyOf,
    }),
    options: {
      ...value.options,
      disabled: originalProperty.options?.disabled,
      required: originalProperty.options?.required,
    },
  };
}

// relaxes required constraints so optional step-1 defaults can be left empty
function relaxPropertyConstraints(value: JsonSchema): JsonSchema {
  return {
    ...value,
    ...(value.minItems !== undefined && { minItems: 0 }),

    ...(value.minLength !== undefined && { minLength: 0 }),

    ...(value.type === 'string' &&
      (value.format === 'date' || value.format === 'datetime') && {
        type: undefined,
        anyOf: [{ type: 'string' }, { type: 'null' }],
      }),
    options: {
      ...value.options,
      required: false,
    },
  };
}

// disables a property by setting disabled: true, required: false, and minItems: 0
function disableProperty(value: JsonSchema): JsonSchema {
  const relaxed = relaxPropertyConstraints(value);
  return {
    ...relaxed,
    options: {
      ...relaxed.options,
      disabled: true,
    },
  };
}

// transforms properties and collects keys whose required constraints are not
// enforced on step 1
function transformProperties(
  properties: JsonSchema['properties'],
  jsonSchema: JsonSchema,
  shouldDisableKey: (key: string, value: JsonSchema) => boolean,
  shouldRelaxKey: (key: string, value: JsonSchema) => boolean
): {
  properties: JsonSchema['properties'];
  nonEnforcedKeys: Set<string>;
} {
  const nonEnforcedKeys = new Set<string>();
  if (!properties) {
    return { properties, nonEnforcedKeys };
  }
  const transformed = Object.entries(properties).reduce(
    (acc, [key, value]) => {
      if (typeof value !== 'object') {
        acc[key] = value;
        return acc;
      }

      if (shouldDisableKey(key, value)) {
        acc[key] = disableProperty(value);
        nonEnforcedKeys.add(key);
        return acc;
      }

      if (shouldRelaxKey(key, value)) {
        // Relax the live editor property so plugin-added data (e.g. conditional
        // filter enum options) is preserved. Strip disabled so a previous AI /
        // filename disable pass does not leave the field read-only.
        const relaxed = relaxPropertyConstraints(value);
        const { disabled: _disabled, ...options } = relaxed.options ?? {};
        acc[key] = {
          ...relaxed,
          options: {
            ...options,
            required: false,
          },
        };
        nonEnforcedKeys.add(key);
        return acc;
      }

      const originalSchemaPropertyValue = jsonSchema.properties?.[key];
      acc[key] = originalSchemaPropertyValue
        ? reassignOriginalProperties(value, originalSchemaPropertyValue)
        : value;
      return acc;
    },
    {} as { [key: string]: JsonSchema | boolean }
  );
  return { properties: transformed, nonEnforcedKeys };
}

function filterAllOfForNonEnforcedKeys(
  allOf: JsonSchema['allOf'],
  nonEnforcedKeys: Set<string>
): JsonSchema['allOf'] {
  if (!allOf || nonEnforcedKeys.size === 0) {
    return allOf;
  }
  return allOf.filter(
    (entry) => !allOfEntryTargetsAnyKey(entry, nonEnforcedKeys)
  );
}

/**
 * !! IMPORTANT !!
 * `relaxPropertyConstraints` / `disableProperty` relax required constraints
 * (type, anyOf, minLength, minItems) so disabled or optional step-1 fields accept
 * null/empty values. `reassignOriginalProperties` reverses those changes when a
 * field is re-enabled. All three must stay in sync with `getPropertySchema` /
 * `getType` / `getMinLength` / `getMinItems` in
 * `apps/portal-gateway/src/modules/table/jsonSchemaHelpers.ts`.
 * If the gateway changes how required vs non-required schemas are built, update
 * these functions here (and the matching gateway comment there).
 */
export const useGenerateSchemaDisablingMetaDataExtractionKeys = ({
  fileNameMetadataExtractionConfig,
  editorJsonSchema,
  extractionMethod,
  jsonSchema,
}: {
  fileNameMetadataExtractionConfig?: FileUploadMetaDataExtractionDto;
  editorJsonSchema: JsonSchema;
  extractionMethod: METADATA_EXTRACTION_TYPE;
  jsonSchema: JsonSchema;
}): JsonSchema => {
  return useMemo(() => {
    const updatedSchema = structuredClone(editorJsonSchema);
    let shouldDisableKey: (key: string, value: JsonSchema) => boolean = (key) =>
      key === fileNameMetadataExtractionConfig?.key;
    let shouldRelaxKey: (key: string, value: JsonSchema) => boolean = () =>
      false;

    switch (extractionMethod) {
      case METADATA_EXTRACTION_FROM_USER_VALUE:
        shouldDisableKey = () => false;
        shouldRelaxKey = (_key, value) =>
          value.options?.perFileInputInUpload === true;
        break;
      case METADATA_EXTRACTION_FROM_AI_VALUE:
        shouldDisableKey = (_key, value) =>
          value.options?.perFileInputInUpload === true;
        break;
      default:
        break;
    }

    const { properties, nonEnforcedKeys } = transformProperties(
      updatedSchema.properties,
      jsonSchema,
      shouldDisableKey,
      shouldRelaxKey
    );
    updatedSchema.properties = properties;
    if (jsonSchema.allOf) {
      updatedSchema.allOf = filterAllOfForNonEnforcedKeys(
        jsonSchema.allOf,
        nonEnforcedKeys
      );
    }

    return updatedSchema;
  }, [
    fileNameMetadataExtractionConfig?.key,
    extractionMethod,
    jsonSchema,
    editorJsonSchema,
  ]);
};
